import axios, { AxiosHeaders } from 'axios';
import { auth } from '@/store/authStore';
import { attachSanitizedErrorInterceptor } from './errorSanitizer';
import { handleExpiredAdminSession } from '@/utils/sessionLogout';

export type EventStatus = 'draft' | 'published' | 'cancelled';
export type PaymentMethod = 'card' | 'cash';
export type PaymentStatus = 'pending' | 'paid';
export type AttendanceStatus = 'coming' | 'not_coming';
export type SessionAttendanceStatus = 'not_arrived' | 'checked_in' | 'checked_out' | 'absent';
export type TicketMode = 'all_sessions' | 'specific_sessions';
export type DiscountType = 'code' | 'multi_attendee' | 'multi_purchase';
export type DiscountAmountType = 'percentage' | 'fixed';

export interface OwnerDiscount {
  _id: string;
  ownerId: string;
  name: string;
  type: DiscountType;
  amountType: DiscountAmountType;
  amountValue: number;
  code?: string;
  minAttendees?: number;
  minSessionsPerAttendee?: number;
  minBookingTotalCents?: number;
  appliesToAllActivities: boolean;
  activityIds: string[];
  startsAt?: string;
  endsAt?: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AppliedDiscount {
  discountId: string;
  name: string;
  type: DiscountType;
  code?: string;
  amountCents: number;
  summary: string;
  automatic: boolean;
}

export interface PublicAutomaticDiscount {
  id: string;
  name: string;
  type: 'multi_attendee' | 'multi_purchase';
  amountType: DiscountAmountType;
  amountValue: number;
  minAttendees?: number;
  minSessionsPerAttendee?: number;
  summary: string;
}

export interface EventSession {
  id: string;
  startDateTime: string;
  endDateTime: string;
  capacity: number;
  bookingEnabled?: boolean;
  bookingCutoffHours?: number;
  bookedSpots?: number;
  spotsRemaining?: number;
}

export interface EventTicketType {
  id: string;
  name: string;
  mode: TicketMode;
  priceCents: number;
  sessionIds?: string[];
}

export interface EventAddOn {
  id: string;
  name: string;
  priceCents: number;
}

export interface EventNotificationSettings {
  emailEnabled: boolean;
  smsEnabled: boolean;
  emailLeadMinutes: number;
  smsLeadMinutes: number;
}

export interface ClubProfile {
  _id?: string;
  ownerId: string;
  clubName: string;
  description?: string;
  contactEmail?: string;
  contactPhone?: string;
  websiteUrl?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  postcode?: string;
  country?: string;
  logoUrl?: string;
  bookingTerms?: string;
  showClubTitle?: boolean;
  showInPublicList?: boolean;
  publicSlug: string;
}

export type UpsertMyClubProfilePayload = {
  publicSlug: string;
  clubName?: string;
  description?: string;
  contactEmail?: string;
  contactPhone?: string;
  websiteUrl?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  postcode?: string;
  country?: string;
  logoUrl?: string;
  bookingTerms?: string;
  showClubTitle?: boolean;
  showInPublicList?: boolean;
};

export interface OwnerEvent {
  _id: string;
  title: string;
  description?: string;
  location: string;
  venueId?: string;
  venueName?: string;
  venueAddressLine1?: string;
  venueAddressLine2?: string;
  venueCity?: string;
  venuePostcode?: string;
  startDate: string;
  endDate: string;
  capacity: number;
  priceCents: number;
  currency: string;
  paymentMethods?: PaymentMethod[];
  status: EventStatus;
  publicSlug: string;
  subtitle?: string;
  sessions?: EventSession[];
  ticketTypes?: EventTicketType[];
  addOns?: EventAddOn[];
  notificationSettings?: EventNotificationSettings;
  waitlistEnabled?: boolean;
  bookedSpots: number;
  bookingCount: number;
  spotsRemaining: number;
}

export interface EventOverviewAttendee {
  bookingId: string;
  parentName: string;
  parentEmail?: string;
  childId?: string;
  childName: string;
  childAge?: number;
  childDateOfBirth?: string;
  childIndex: number;
  ticketName: string;
  addOns: string[];
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  attendanceStatus: AttendanceStatus;
  sessionAttendanceStatus: SessionAttendanceStatus;
  checkedInAt?: string | null;
  checkedOutAt?: string | null;
  quantity: number;
}

export interface EventOverviewSession {
  id: string;
  startDateTime: string;
  endDateTime: string;
  capacity: number;
  bookedSpots: number;
  spotsRemaining: number;
  checkedInCount: number;
  checkedOutCount: number;
  absentCount: number;
  attendees: EventOverviewAttendee[];
}

export interface OwnerEventOverview {
  event: {
    id: string;
    title: string;
    subtitle?: string;
    location: string;
    status: EventStatus;
    currency: string;
    ticketTypes: EventTicketType[];
  };
  sessions: EventOverviewSession[];
}

export interface PublicClubProfile {
  clubName: string;
  description?: string;
  contactEmail?: string;
  contactPhone?: string;
  websiteUrl?: string;
  logoUrl?: string;
  bookingTerms?: string;
  showClubTitle?: boolean;
  showInPublicList?: boolean;
  publicSlug: string;
}

export interface PublicEvent {
  _id: string;
  title: string;
  description?: string;
  location: string;
  startDate: string;
  endDate: string;
  capacity: number;
  priceCents: number;
  currency: string;
  paymentMethods?: PaymentMethod[];
  cardPaymentsAvailable?: boolean;
  cardPaymentsSetupRequired?: boolean;
  publicSlug: string;
  subtitle?: string;
  sessions?: EventSession[];
  ticketTypes?: EventTicketType[];
  addOns?: EventAddOn[];
  notificationSettings?: EventNotificationSettings;
  waitlistEnabled?: boolean;
  bookedSpots: number;
  spotsRemaining: number;
  automaticDiscounts?: PublicAutomaticDiscount[];
  clubProfile: PublicClubProfile | null;
}

export interface ParentWaitlistItem {
  id: string;
  sessionId: string;
  status: 'active' | 'notified';
  notifiedCount: number;
  lastNotifiedAt?: string | null;
  createdAt: string;
}

export interface ParentWaitlistResponse {
  eventId: string;
  sessionIds: string[];
  items: ParentWaitlistItem[];
}

export type PublicClubEvent = Omit<PublicEvent, 'clubProfile'>;

export interface PublicClubEventsResponse {
  clubProfile: PublicClubProfile;
  items: PublicClubEvent[];
}

export interface PublicClubBrowseItem {
  ownerId: string;
  clubName: string;
  description?: string;
  logoUrl?: string;
  publicSlug: string;
  activityCount: number;
  nextDate?: string | null;
}

export interface ParentChildProfile {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
}

export interface Booking {
  _id: string;
  eventId: string;
  sessionIds?: string[];
  ticketTypeId?: string;
  addOnSelections?: Array<{ addOnId: string; sessionIds: string[]; childIndexes?: number[] }>;
  cartItems?: Array<{
    ticketTypeId: string;
    sessionIds: string[];
    childIndexes?: number[];
    addOnSelections?: Array<{ addOnId: string; sessionIds: string[]; childIndexes?: number[] }>;
    quantity?: number;
  }>;
  ownerId: string;
  parentName: string;
  parentEmail?: string;
  parentPhone?: string;
  childName: string;
  quantity: number;
  unitPriceCents: number;
  totalPriceCents: number;
  preDiscountTotalCents?: number;
  discountTotalCents?: number;
  appliedDiscounts?: AppliedDiscount[];
  currency: string;
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  attendanceStatus: AttendanceStatus;
  paymentReference?: string;
  parentUserId?: string;
  parentAccountId?: string;
  children?: ParentChildProfile[];
  sessionAttendance?: Array<{
    sessionId: string;
    childId?: string;
    childIndex: number;
    status: SessionAttendanceStatus;
    checkedInAt?: string;
    checkedOutAt?: string;
  }>;
  notes?: string;
  createdAt: string;
}

export interface OwnerBookingDetailResponse {
  booking: Booking;
  event: {
    id: string;
    title: string;
    location: string;
    currency: string;
    sessions?: EventSession[];
    ticketTypes?: EventTicketType[];
    addOns?: EventAddOn[];
  } | null;
}

export interface CreatePublicBookingResponse {
  booking: Booking;
  message: string;
  requiresCheckout?: boolean;
  checkoutUrl?: string;
  guestEmailSent?: boolean;
  parentEmailNoticeType?: 'activation' | 'existing_account';
}

export interface CreateManualBookingResponse {
  booking: Booking;
  message: string;
}

export interface RevenueSummary {
  totalRevenueCents: number;
  currency: string;
}

export interface OwnerDashboardSummary {
  totals: {
    activities: number;
    bookings: number;
    stripeRevenueCents: number;
    paidCashRevenueCents: number;
    sessionsBooked: number;
    sessionsAttended: number;
  };
  revenueByMonth: Array<{
    key: string;
    label: string;
    stripeRevenueCents: number;
    cashRevenueCents: number;
  }>;
}

export interface ParentProfile {
  id: string;
  name: string;
  email: string;
  phone?: string;
  children: ParentChildProfile[];
  hasPassword: boolean;
}

export interface CompletedBookingResponse {
  booking: Booking;
  event: {
    id: string;
    title: string;
    location: string;
    startDate: string;
    endDate: string;
    currency: string;
    sessions?: EventSession[];
    ticketTypes?: EventTicketType[];
    addOns?: EventAddOn[];
  };
  checkout: {
    sessionId: string;
    paymentStatus: string;
  };
  requiresParentActivation?: boolean;
  parentEmailNoticeType?: 'activation' | 'existing_account';
}

export interface StripeConnectStatus {
  hasAccount: boolean;
  accountId?: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  onboardingComplete: boolean;
}

export interface Attendee {
  _id: string;
  ownerId: string;
  eventId?: string;
  firstName: string;
  lastName: string;
  parentName: string;
  parentEmail?: string;
  parentPhone?: string;
  notes?: string;
  attendanceStatus: AttendanceStatus;
  createdAt?: string;
}

export interface AttendeeOverviewRow extends Attendee {
  sessionsBooked: number;
  sessionsAttended: number;
}

export interface AttendeeHistoryResponse {
  attendee: Attendee & { fullName: string };
  summary: {
    totalBookings: number;
    totalSessionsBooked: number;
    totalSessionsAttended: number;
  };
  history: Array<{
    bookingId: string;
    bookingReference: string;
    createdAt: string;
    paymentMethod: PaymentMethod;
    paymentStatus: PaymentStatus;
    attendanceStatus: AttendanceStatus;
    quantity: number;
    totalPriceCents: number;
    currency: string;
    event: {
      id: string;
      title: string;
      location: string;
    } | null;
    sessionsBooked: number;
    sessionsAttended: number;
    sessions: Array<{
      sessionId: string;
      startDateTime: string | null;
      endDateTime: string | null;
      sessionAttendanceStatus: SessionAttendanceStatus;
      checkedInAt: string | null;
      checkedOutAt: string | null;
    }>;
  }>;
}

export interface Venue {
  _id: string;
  ownerId: string;
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  postcode: string;
  country?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface OrganizationUser {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'user';
  createdAt: string;
  isOrganizationOwner: boolean;
}
interface AuthResponse {
  accessToken: string;
  id: string;
  name: string;
  email?: string;
  role?: 'admin' | 'user' | 'parent';
  subscriptionAccess?: AdminSubscriptionAccessStatus;
}

export interface AdminSubscriptionAccessStatus {
  accountOwnerId: string;
  accessGranted: boolean;
  hasActiveSubscription: boolean;
  subscriptionStatus: string;
  cancelAtPeriodEnd: boolean;
  subscriptionEndsAt?: string;
  subscriptionSecondsRemaining: number;
  trialStartedAt?: string;
  trialEndsAt?: string;
  trialActive: boolean;
  trialDaysRemaining: number;
  trialSecondsRemaining: number;
}

export interface BillingCheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface BillingPortalSessionResponse {
  url: string;
}

export interface AdminReferralOverview {
  accountOwnerId: string;
  referralCode: string;
  totalReferredAccounts: number;
  activeSubscriberReferrals: number;
  items: Array<{
    id: string;
    name: string;
    email: string;
    companyName?: string;
    role: 'admin' | 'user' | 'parent';
    referredByCode?: string;
    createdAt: string;
    hasActiveSubscription: boolean;
    subscriptionStatus: string;
  }>;
}

export interface PublicReferralProfile {
  found: boolean;
  referralCode: string;
  displayName?: string;
  clubName?: string;
  referrerName?: string;
}

class ClubsBookingApi {
  private client = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/v1',
    headers: { 'Content-Type': 'application/json' },
  });

  constructor() {
    this.client.interceptors.request.use((config) => {
      const token = auth.getToken();
      const headers = AxiosHeaders.from(config.headers || {});

      if (token) {
        if (auth.isTokenExpired(token)) {
          this.clearAuthToken();
          handleExpiredAdminSession();
          return Promise.reject(new axios.Cancel('Admin session expired'));
        }
        headers.set('Authorization', `Bearer ${token}`);
      } else {
        headers.delete('Authorization');
      }

      config.headers = headers;

      return config;
    });

    attachSanitizedErrorInterceptor(this.client);

    const token = auth.getToken();
    if (token) {
      this.setAuthToken(token);
    }
  }

  setAuthToken(token: string) {
    this.client.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  clearAuthToken() {
    delete this.client.defaults.headers.common.Authorization;
  }

  async login(email: string, password: string): Promise<AuthResponse> {
    const { data } = await this.client.post<AuthResponse>('/auth/login', { email, password });
    return data;
  }

  async parentLogin(email: string, password: string): Promise<AuthResponse> {
    const { data } = await this.client.post<AuthResponse>('/parent-auth/login', { email, password });
    return data;
  }

  async parentSignup(payload: {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
  }): Promise<AuthResponse> {
    const { data } = await this.client.post<AuthResponse>('/parent-auth/signup', payload);
    return data;
  }

  async resendParentActivationLink(email: string): Promise<{ sent: boolean; alreadyActive?: boolean; message?: string }> {
    const { data } = await this.client.post<{ sent: boolean; alreadyActive?: boolean; message?: string }>(
      '/parent-auth/resend-activation',
      { email },
    );
    return data;
  }

  async parentActivate(token: string, password: string): Promise<AuthResponse> {
    const { data } = await this.client.post<AuthResponse>('/parent-auth/activate', { token, password });
    return data;
  }

  async getParentProfile(): Promise<ParentProfile> {
    const { data } = await this.client.get<ParentProfile>('/parent-auth/me');
    return data;
  }

  async updateParentProfile(payload: Partial<{
    name: string;
    phone: string;
    children: Array<{
      id?: string;
      firstName: string;
      lastName: string;
      dateOfBirth: string;
    }>;
  }>): Promise<ParentProfile> {
    const { data } = await this.client.patch<ParentProfile>('/parent-auth/me', payload);
    return data;
  }

  async signup(payload: {
    email: string;
    name: string;
    password: string;
    confirmPassword: string;
    companyName?: string;
    referralCode?: string;
  }): Promise<AuthResponse> {
    const { data } = await this.client.post<AuthResponse>('/auth/signup', payload);
    return data;
  }

  async getAdminReferralOverview(): Promise<AdminReferralOverview> {
    const { data } = await this.client.get<AdminReferralOverview>('/auth/referrals');
    return data;
  }

  async getPublicReferralProfile(referralCode: string): Promise<PublicReferralProfile> {
    const encodedCode = encodeURIComponent(String(referralCode || '').trim());
    const { data } = await this.client.get<PublicReferralProfile>(`/auth/referrals/public/${encodedCode}`);
    return data;
  }
  async listOrganizationUsers(): Promise<OrganizationUser[]> {
    const { data } = await this.client.get<OrganizationUser[]>('/auth/users');
    return data;
  }

  async createOrganizationUser(payload: {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
    role: 'admin' | 'user';
  }): Promise<OrganizationUser> {
    const { data } = await this.client.post<OrganizationUser>('/auth/users', payload);
    return data;
  }

  async getOrganizationUser(userId: string): Promise<{ data: OrganizationUser & { companyName?: string; destinationEmail?: string } }> {
    const { data } = await this.client.get<{ data: OrganizationUser & { companyName?: string; destinationEmail?: string } }>(`/auth/user/${userId}`);
    return data;
  }

  async updateOrganizationUser(
    userId: string,
    payload: {
      name: string;
      email: string;
      role: 'admin' | 'user';
      password?: string;
      confirmPassword?: string;
    },
  ): Promise<OrganizationUser> {
    const { data } = await this.client.patch<OrganizationUser>(`/auth/users/${userId}`, payload);
    return data;
  }

  async getMyClubProfile(): Promise<ClubProfile | null> {
    const { data } = await this.client.get<ClubProfile | null>('/club-profile/me');
    return data;
  }

  async upsertMyClubProfile(payload: UpsertMyClubProfilePayload) {
    const { data } = await this.client.put<ClubProfile>('/club-profile/me', payload);
    return data;
  }

  async getStripeConnectStatus(): Promise<StripeConnectStatus> {
    const { data } = await this.client.get<StripeConnectStatus>('/billing/connect/status');
    return data;
  }

  async createStripeConnectOnboardingLink(payload?: { returnUrl?: string; refreshUrl?: string }): Promise<{ url: string; accountId: string }> {
    const { data } = await this.client.post<{ url: string; accountId: string }>('/billing/connect/onboarding-link', payload || {});
    return data;
  }

  async createStripeConnectDashboardLink(): Promise<{ url: string }> {
    const { data } = await this.client.post<{ url: string }>('/billing/connect/dashboard-link', {});
    return data;
  }

  async getAdminSubscriptionAccessStatus(syncWithStripe = true, forceSync = false): Promise<AdminSubscriptionAccessStatus> {
    const { data } = await this.client.get<AdminSubscriptionAccessStatus>('/billing/subscription/access', {
      params: {
        syncWithStripe: syncWithStripe ? 'true' : 'false',
        forceSync: forceSync ? 'true' : 'false',
      },
    });
    return data;
  }

  async createAdminSubscriptionCheckout(payload?: { successUrl?: string; cancelUrl?: string }): Promise<BillingCheckoutSessionResponse> {
    const { data } = await this.client.post<BillingCheckoutSessionResponse>(
      '/billing/subscription/checkout',
      payload || {},
    );
    return data;
  }

  async createAdminSubscriptionPortalLink(payload?: { returnUrl?: string }): Promise<BillingPortalSessionResponse> {
    const { data } = await this.client.post<BillingPortalSessionResponse>(
      '/billing/subscription/portal-link',
      payload || {},
    );
    return data;
  }

  async listMyEvents(
    status?: EventStatus,
    options?: { page?: number; limit?: number },
  ): Promise<PaginatedResponse<OwnerEvent>> {
    const { data } = await this.client.get<PaginatedResponse<OwnerEvent>>('/events/mine', {
      params: {
        status,
        page: options?.page,
        limit: options?.limit,
      },
    });
    return data;
  }

  async getMyEvent(eventId: string): Promise<OwnerEvent> {
    const { data } = await this.client.get<OwnerEvent>(`/events/mine/${eventId}`);
    return data;
  }

  async getMyEventOverview(eventId: string): Promise<OwnerEventOverview> {
    const { data } = await this.client.get<OwnerEventOverview>(`/events/mine/${eventId}/overview`);
    return data;
  }

  async createEvent(payload: {
    title: string;
    subtitle?: string;
    description?: string;
    location?: string;
    venueId?: string;
    startDate: string;
    endDate: string;
    capacity: number;
    priceCents: number;
    currency?: string;
    paymentMethods?: PaymentMethod[];
    status?: EventStatus;
    publicSlug?: string;
    sessions?: EventSession[];
    ticketTypes?: EventTicketType[];
    addOns?: EventAddOn[];
    notificationSettings?: EventNotificationSettings;
    waitlistEnabled?: boolean;
  }) {
    const { data } = await this.client.post('/events', payload);
    return data;
  }

  async updateEvent(eventId: string, payload: Partial<{
    title: string;
    subtitle: string;
    description?: string;
    location?: string;
    venueId?: string;
    startDate: string;
    endDate: string;
    capacity: number;
    priceCents: number;
    currency: string;
    paymentMethods: PaymentMethod[];
    status: EventStatus;
    publicSlug: string;
    sessions: EventSession[];
    ticketTypes: EventTicketType[];
    addOns: EventAddOn[];
    notificationSettings: EventNotificationSettings;
    waitlistEnabled: boolean;
  }>) {
    const { data } = await this.client.patch(`/events/mine/${eventId}`, payload);
    return data;
  }

  async listMyDiscounts(params?: { type?: DiscountType; page?: number; limit?: number }): Promise<PaginatedResponse<OwnerDiscount>> {
    const { data } = await this.client.get<PaginatedResponse<OwnerDiscount>>('/discounts/mine', {
      params,
    });
    return data;
  }

  async getMyDiscount(discountId: string): Promise<OwnerDiscount> {
    const { data } = await this.client.get<OwnerDiscount>(`/discounts/mine/${discountId}`);
    return data;
  }

  async createDiscount(payload: {
    name: string;
    type: DiscountType;
    amountType: DiscountAmountType;
    amountValue: number;
    code?: string;
    minAttendees?: number;
    minSessionsPerAttendee?: number;
    minBookingTotalCents?: number;
    appliesToAllActivities?: boolean;
    activityIds?: string[];
    startsAt?: string;
    endsAt?: string;
    enabled?: boolean;
  }): Promise<OwnerDiscount> {
    const { data } = await this.client.post<OwnerDiscount>('/discounts/mine', payload);
    return data;
  }

  async updateDiscount(discountId: string, payload: Partial<{
    name: string;
    type: DiscountType;
    amountType: DiscountAmountType;
    amountValue: number;
    code?: string;
    minAttendees?: number;
    minSessionsPerAttendee?: number;
    minBookingTotalCents?: number;
    appliesToAllActivities?: boolean;
    activityIds?: string[];
    startsAt?: string;
    endsAt?: string;
    enabled?: boolean;
  }>): Promise<OwnerDiscount> {
    const { data } = await this.client.patch<OwnerDiscount>(`/discounts/mine/${discountId}`, payload);
    return data;
  }

  async deleteDiscount(discountId: string): Promise<{ deleted: true }> {
    const { data } = await this.client.delete<{ deleted: true }>(`/discounts/mine/${discountId}`);
    return data;
  }

  async deleteEvent(eventId: string): Promise<{ deleted: true }> {
    const { data } = await this.client.delete<{ deleted: true }>(`/events/mine/${eventId}`);
    return data;
  }

  async listMyVenues(): Promise<PaginatedResponse<Venue>> {
    const { data } = await this.client.get<PaginatedResponse<Venue>>('/venues/mine');
    return data;
  }

  async createVenue(payload: {
    name: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    postcode: string;
    country?: string;
  }): Promise<Venue> {
    const { data } = await this.client.post<Venue>('/venues/mine', payload);
    return data;
  }

  async updateVenue(venueId: string, payload: {
    name?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    postcode?: string;
    country?: string;
  }): Promise<Venue> {
    const { data } = await this.client.patch<Venue>(`/venues/mine/${venueId}`, payload);
    return data;
  }

  async listMyBookings(
    eventId?: string,
    options?: { page?: number; limit?: number },
  ): Promise<PaginatedResponse<Booking>> {
    const { data } = await this.client.get<PaginatedResponse<Booking>>('/bookings/mine', {
      params: {
        eventId,
        page: options?.page,
        limit: options?.limit,
      },
    });
    return data;
  }

  async getMyBooking(bookingId: string): Promise<OwnerBookingDetailResponse> {
    const { data } = await this.client.get<OwnerBookingDetailResponse>(`/bookings/mine/${bookingId}`);
    return data;
  }

  async deleteMyBooking(bookingId: string): Promise<{ deleted: boolean; deletedAttendees: number }> {
    const { data } = await this.client.delete<{ deleted: boolean; deletedAttendees: number }>(`/bookings/mine/${bookingId}`);
    return data;
  }

  async updateBookingPayment(bookingId: string, payload: { paymentStatus: PaymentStatus; paymentReference?: string }) {
    const { data } = await this.client.patch(`/bookings/mine/${bookingId}/payment`, payload);
    return data;
  }

  async updateBookingAttendance(bookingId: string, payload: { attendanceStatus: AttendanceStatus }) {
    const { data } = await this.client.patch(`/bookings/mine/${bookingId}/attendance`, payload);
    return data;
  }

  async updateBookingSessionAttendance(
    bookingId: string,
    payload: {
      sessionId: string;
      childIndex: number;
      status: SessionAttendanceStatus;
    },
  ) {
    const { data } = await this.client.patch(`/bookings/mine/${bookingId}/session-attendance`, payload);
    return data;
  }

  async createManualBooking(payload: {
    eventId: string;
    sessionId: string;
    childFirstName: string;
    childLastName: string;
    childDateOfBirth?: string;
    parentName?: string;
    amountCents: number;
  }): Promise<CreateManualBookingResponse> {
    const { data } = await this.client.post<CreateManualBookingResponse>('/bookings/mine/manual', payload);
    return data;
  }

  async getRevenueSummary(): Promise<RevenueSummary> {
    const { data } = await this.client.get<RevenueSummary>('/bookings/mine/revenue');
    return data;
  }

  async getOwnerDashboardSummary(): Promise<OwnerDashboardSummary> {
    const { data } = await this.client.get<OwnerDashboardSummary>('/bookings/mine/dashboard-summary');
    return data;
  }

  async getPublicEvent(eventSlug: string): Promise<PublicEvent> {
    const encodedSlug = encodeURIComponent(eventSlug.trim());
    const { data } = await this.client.get<PublicEvent>(`/events/public/${encodedSlug}`);
    return data;
  }

  async getPublicClubEvents(clubSlug: string): Promise<PublicClubEventsResponse> {
    const encodedSlug = encodeURIComponent(clubSlug.trim());
    const { data } = await this.client.get<PublicClubEventsResponse>(`/events/public/club/${encodedSlug}`);
    return data;
  }

  async listPublicClubs(): Promise<{ items: PublicClubBrowseItem[] }> {
    const { data } = await this.client.get<{ items: PublicClubBrowseItem[] }>('/events/public/clubs');
    return data;
  }

  async createPublicBooking(eventSlug: string, payload: {
    parentName: string;
    parentEmail: string;
    parentPhone?: string;
    children: Array<{
      id?: string;
      firstName: string;
      lastName: string;
      dateOfBirth: string;
    }>;
    paymentMethod?: PaymentMethod;
    paymentReference?: string;
    parentPassword?: string;
    checkoutMode?: 'guest' | 'login';
    ticketTypeId?: string;
    selectedSessionIds?: string[];
    addOnSelections?: Array<{ addOnId: string; sessionIds: string[]; childIndexes?: number[] }>;
    cartItems?: Array<{
      ticketTypeId: string;
      selectedSessionIds?: string[];
      childIndexes?: number[];
      addOnSelections?: Array<{ addOnId: string; sessionIds: string[]; childIndexes?: number[] }>;
      quantity?: number;
    }>;
    notes?: string;
    discountCode?: string;
  }): Promise<CreatePublicBookingResponse> {
    const encodedSlug = encodeURIComponent(eventSlug.trim());
    const { data } = await this.client.post<CreatePublicBookingResponse>(`/bookings/public/${encodedSlug}`, payload);
    return data;
  }

  async confirmCompletedPublicBooking(sessionId?: string, bookingId?: string): Promise<CompletedBookingResponse> {
    const { data } = await this.client.get<CompletedBookingResponse>('/bookings/public/completed', {
      params: { sessionId, bookingId },
    });
    return data;
  }

  async listMyAttendees(eventId?: string, options?: { page?: number; limit?: number }): Promise<PaginatedResponse<Attendee>> {
    const { data } = await this.client.get<PaginatedResponse<Attendee>>('/attendees/mine', {
      params: {
        eventId,
        page: options?.page,
        limit: options?.limit,
      },
    });
    return data;
  }

  async listMyAttendeeOverview(
    eventId?: string,
    options?: { page?: number; limit?: number },
  ): Promise<PaginatedResponse<AttendeeOverviewRow>> {
    const { data } = await this.client.get<PaginatedResponse<AttendeeOverviewRow>>('/attendees/mine/overview', {
      params: {
        eventId,
        page: options?.page,
        limit: options?.limit,
      },
    });
    return data;
  }

  async getMyAttendeeHistory(attendeeId: string): Promise<AttendeeHistoryResponse> {
    const { data } = await this.client.get<AttendeeHistoryResponse>(`/attendees/mine/${attendeeId}/history`);
    return data;
  }

  async createAttendee(payload: {
    eventId?: string;
    firstName: string;
    lastName: string;
    parentName: string;
    parentEmail: string;
    parentPhone?: string;
    notes?: string;
  }): Promise<Attendee> {
    const { data } = await this.client.post<Attendee>('/attendees/mine', payload);
    return data;
  }

  async updateAttendee(attendeeId: string, payload: Partial<Attendee>): Promise<Attendee> {
    const { data } = await this.client.patch<Attendee>(`/attendees/mine/${attendeeId}`, payload);
    return data;
  }

  async deleteAttendee(attendeeId: string): Promise<Attendee> {
    const { data } = await this.client.delete<Attendee>(`/attendees/mine/${attendeeId}`);
    return data;
  }

  async listParentBookings(): Promise<Array<Booking & {
    event: {
      id: string;
      title: string;
      publicSlug: string;
      startDate: string;
      endDate: string;
      location: string;
      sessions?: Array<{ id: string; startDateTime: string; endDateTime: string }>;
    } | null
  }>> {
    const { data } = await this.client.get<Array<Booking & {
      event: {
        id: string;
        title: string;
        publicSlug: string;
        startDate: string;
        endDate: string;
        location: string;
        sessions?: Array<{ id: string; startDateTime: string; endDateTime: string }>;
      } | null
    }>>('/bookings/parent/mine');
    return data;
  }

  async listParentWaitlist(eventSlug: string): Promise<ParentWaitlistResponse> {
    const encodedSlug = encodeURIComponent(eventSlug.trim());
    const { data } = await this.client.get<ParentWaitlistResponse>(`/bookings/parent/waitlist/${encodedSlug}`);
    return data;
  }

  async joinParentWaitlist(eventSlug: string, payload: { sessionId: string }): Promise<{
    joined: boolean;
    alreadyJoined: boolean;
    message: string;
    entryId: string;
    position?: number;
  }> {
    const encodedSlug = encodeURIComponent(eventSlug.trim());
    const { data } = await this.client.post(`/bookings/parent/waitlist/${encodedSlug}`, payload);
    return data;
  }
}

export const clubsBookingApi = new ClubsBookingApi();

